import { useState } from "react";
import { Link } from "react-router-dom";
import { useVote } from "../hooks/useVote.js";
import {
  VIBE_EMOJI_LABELS,
  VIBE_BG_COLORS,
  HORIZON_LABELS,
  REASON_TAGS,
} from "../lib/constants.js";
import TurnstileWidget from "./TurnstileWidget.js";

interface VoteWidgetProps {
  startupId: string;
  startupName: string;
  onVoted?: (result: any) => void;
}

export default function VoteWidget({
  startupId,
  startupName,
  onVoted,
}: VoteWidgetProps) {
  const { loading, error, voted, result, vote } = useVote();
  const [selectedVibe, setSelectedVibe] = useState<number | null>(null);
  const [selectedHorizon, setSelectedHorizon] = useState<number | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>("");

  async function handleSubmit() {
    if (selectedVibe === null) return;
    if (!turnstileToken) return;

    try {
      const res = await vote({
        startup_id: startupId,
        vibe: selectedVibe,
        horizon_bucket: selectedHorizon,
        reason_tag: selectedTag,
        turnstile_token: turnstileToken,
      });
      onVoted?.(res);
    } catch {
      // Error is in state
    }
  }

  if (voted && result) {
    return (
      <YouVsCrowd
        result={result}
        userVibe={selectedVibe!}
        startupName={startupName}
      />
    );
  }

  const vibeValues = [2, 1, 0, -1, -2];
  const horizonValues = [1, 2, 3, 4, 5];

  return (
    <div className="card-glow space-y-6">
      <h3 className="font-bold text-lg">
        What's your vibe on{" "}
        <span className="gradient-text">{startupName}</span>?
      </h3>

      {/* Step 1: Vibe */}
      <div>
        <label className="text-sm text-slate-400 mb-3 block font-medium">
          Your vibe
        </label>
        <div className="flex gap-2 flex-wrap">
          {vibeValues.map((v) => (
            <button
              key={v}
              onClick={() => setSelectedVibe(v)}
              className={`flex-1 min-w-[80px] py-3 px-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                selectedVibe === v
                  ? `${VIBE_BG_COLORS[v]} text-white scale-105 shadow-lg`
                  : "bg-slate-800/60 text-slate-300 hover:bg-slate-700/60 border border-slate-700/30 hover:border-slate-600/50"
              }`}
            >
              {VIBE_EMOJI_LABELS[v]}
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: Survival horizon (optional) */}
      {selectedVibe !== null && (
        <div className="animate-fade-in">
          <label className="text-sm text-slate-400 mb-3 block font-medium">
            Survival horizon{" "}
            <span className="text-slate-600">(optional)</span>
          </label>
          <div className="flex gap-2 flex-wrap">
            {horizonValues.map((h) => (
              <button
                key={h}
                onClick={() =>
                  setSelectedHorizon(selectedHorizon === h ? null : h)
                }
                className={`py-2 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  selectedHorizon === h
                    ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md"
                    : "bg-slate-800/60 text-slate-300 hover:bg-slate-700/60 border border-slate-700/30"
                }`}
              >
                {HORIZON_LABELS[h]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Reason tag (optional) */}
      {selectedVibe !== null && (
        <div className="animate-fade-in">
          <label className="text-sm text-slate-400 mb-3 block font-medium">
            One reason <span className="text-slate-600">(optional)</span>
          </label>
          <div className="flex gap-2 flex-wrap">
            {REASON_TAGS.map((tag) => (
              <button
                key={tag.value}
                onClick={() =>
                  setSelectedTag(selectedTag === tag.value ? null : tag.value)
                }
                className={`py-1.5 px-3 rounded-full text-xs font-medium transition-all duration-200 ${
                  selectedTag === tag.value
                    ? "bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md"
                    : "bg-slate-800/60 text-slate-400 hover:bg-slate-700/60 hover:text-slate-200 border border-slate-700/30"
                }`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Turnstile */}
      <TurnstileWidget onToken={setTurnstileToken} />

      {/* Submit */}
      {selectedVibe !== null && (
        <div className="space-y-3 animate-fade-in">
          {!turnstileToken && (
            <p className="text-amber-400/80 text-sm flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Complete verification to vote
            </p>
          )}
          <div className="flex items-center gap-4">
            <button
              onClick={handleSubmit}
              disabled={loading || !turnstileToken}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                "Cast your vibe \u2728"
              )}
            </button>
          </div>
          {error && (
            <p className="text-red-400 text-sm flex items-center gap-1.5">
              <span>{"⚠️"}</span> {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function YouVsCrowd({
  result,
  userVibe,
  startupName,
}: {
  result: any;
  userVibe: number;
  startupName: string;
}) {
  const crowdVibe = result.startup_aggregates?.vibe_score ?? 0;
  const crowdDisplay = Math.round(((crowdVibe + 2) / 4) * 100);
  const userDisplay = Math.round(((userVibe + 2) / 4) * 100);
  const diff = userDisplay - crowdDisplay;

  return (
    <div className="card-glow space-y-5 animate-scale-in">
      {/* Success header */}
      <div className="text-center">
        <div className="text-4xl mb-2 animate-bounce-in">{"🎉"}</div>
        <h3 className="font-bold text-lg gradient-text">Vibe recorded!</h3>
      </div>

      {/* Score comparison */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-2xl p-4 text-center">
          <div className="text-sm text-indigo-300 mb-1 font-medium">
            Your vibe
          </div>
          <div className="text-4xl font-extrabold text-white">
            {userDisplay}
          </div>
          <div className="text-sm text-slate-400 mt-1">
            {VIBE_EMOJI_LABELS[userVibe]}
          </div>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/30 rounded-2xl p-4 text-center">
          <div className="text-sm text-slate-400 mb-1 font-medium">
            Crowd vibe
          </div>
          <div className="text-4xl font-extrabold text-white">
            {crowdDisplay}
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {result.startup_aggregates?.trusted_vote_count ?? 0} votes
          </div>
        </div>
      </div>

      {/* Comparison text */}
      <div className="text-center">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/40 border border-slate-700/30 text-sm">
          {Math.abs(diff) < 10 ? (
            <>
              <span>{"🤝"}</span>
              <span className="text-slate-300">
                You're aligned with the crowd
              </span>
            </>
          ) : diff > 0 ? (
            <>
              <span>{"🚀"}</span>
              <span className="text-emerald-400">
                You're more bullish than the crowd (+{diff})
              </span>
            </>
          ) : (
            <>
              <span>{"🧊"}</span>
              <span className="text-orange-400">
                You're more bearish than the crowd ({diff})
              </span>
            </>
          )}
        </span>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        <button
          onClick={() => {
            const text = `I just vibed on ${startupName} on StartupVibes! My vibe: ${userDisplay}/100, Crowd: ${crowdDisplay}/100 \u2728`;
            const url = window.location.href;
            window.open(
              `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
              "_blank"
            );
          }}
          className="btn-secondary w-full text-center font-medium"
        >
          Share on X
        </button>
        <Link
          to="/"
          className="btn-ghost w-full text-center text-sm text-slate-400"
        >
          {"\u2190"} Vote on another startup
        </Link>
      </div>
    </div>
  );
}
